import { type ReactNode } from "react";
import {
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import { useDisclosure } from "./useDisclosure";

// DisclosureCard (CN-disclosure) — the "titled fold card" chrome shared by every
// surface that shows an always-visible header and reveals a body block below it:
// Home's WeeklyTargetsCard (this-week meter) and Progress's collapsed widgets.
// Both hand-rolled the identical box + tappable header + chevron + divided body;
// this is the extraction the CN-disclosure debt called for (only useDisclosure —
// the state machine — had been pulled out before). The header CONTENT differs per
// surface (a two-line title/subtitle vs a three-line title + stat row), so the
// caller passes it as `header`; the primitive owns the box, the press target, the
// chevron, the a11y state, and the top-divided body. Row-level disclosures
// (FeedbackRow, ProgramSpine, ComposerEntryRow) keep their own chrome — this is
// the CARD shape, not those.
export type DisclosureCardProps = {
  theme: UiTheme;
  /** Always-visible content inside the tappable header (left of the chevron). */
  header: ReactNode;
  /** Body revealed when expanded (and not disabled). */
  children: ReactNode;
  /** Full a11y label for the header button (the caller composes it from the
   *  same info the sighted header shows — the summary is otherwise silent). */
  accessibilityLabel?: string;
  /** No body to reveal → the header is inert and the chevron is hidden. */
  disabled?: boolean;
  initialExpanded?: boolean;
  /** Controlled mode (PG-57): the caller owns the open state. Progress's
   *  collapsed widgets use it to swap the whole card for the open panel. */
  expanded?: boolean;
  onToggle?: () => void;
  /** Home passes its margin box (makeHomeBoxStyle); Progress omits it (the
   *  scroll container owns spacing). Merged over the shared border/radius box. */
  boxStyle?: StyleProp<ViewStyle>;
  /** Per-surface body spacing (Home rows self-space; Progress wants gap+top). */
  bodyStyle?: StyleProp<ViewStyle>;
};

export function DisclosureCard({
  theme,
  header,
  children,
  accessibilityLabel,
  disabled = false,
  initialExpanded = false,
  expanded: expandedProp,
  onToggle,
  boxStyle,
  bodyStyle,
}: DisclosureCardProps) {
  const styles = makeStyles(theme);
  const own = useDisclosure(initialExpanded);
  const expanded = expandedProp ?? own.expanded;
  const toggle = onToggle ?? own.toggle;
  const open = expanded && !disabled;
  return (
    <View style={[styles.box, boxStyle]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ expanded, disabled }}
        // RNW drops accessibilityState (useDisclosure.ts): this reaches the web.
        aria-expanded={expanded}
        onPress={() => !disabled && toggle()}
        style={({ pressed }) => [
          styles.header,
          pressed && !disabled && styles.pressed,
        ]}
      >
        <View style={styles.headText}>{header}</View>
        {!disabled ? (
          <Icon
            name={expanded ? "chevron-up" : "chevron-down"}
            size={theme.typography.sizes.md}
            color={theme.palette.ink3}
          />
        ) : null}
      </Pressable>
      {open ? <View style={[styles.body, bodyStyle]}>{children}</View> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r } = theme;
  return StyleSheet.create({
    box: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      padding: s.s4,
      // Explicit floor: a short single/two-line header wouldn't clear the 44pt
      // touch target on padding alone the way a denser stat-row header does.
      minHeight: 44,
    },
    pressed: { opacity: 0.7 },
    headText: { flex: 1, gap: s.s1 },
    body: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingHorizontal: s.s4,
      paddingBottom: s.s3,
    },
  });
}
